/**
 * netsuite-form-api.js - Builds and submits NetSuite API payloads.
 * Handles UTM tracking, spam protection, and form analytics.
 * Example of form and class initialization found on 'page.cms-total-rna-seq-library-prep.liquid'
 */

/* ---------------------------------------------------
 * 1. UTILITIES
 * --------------------------------------------------- */

// Removes null/undefined fields; can replace with fallback value
function cleanNulls(obj = {}, fallback = "") {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, value == null ? fallback : value])
  );
}

// Generates a unique ID (used for form submissions)
const generateUniqueId = () => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 10);
  return `${timestamp}${randomPart}`;
};

const parseCheckboxAnswers = (group) => {
  const selected = [];
  const checkboxes = group.querySelectorAll("input[type='checkbox']");
  checkboxes.forEach(box => {
    if (box.checked) {
      selected.push(box.value);
      const otherInput = box.closest('.form-checkbox-container')?.querySelector('.other-input');
      if (otherInput && otherInput.value.trim()) {
        selected.push(otherInput.value.trim());
      }
    }
  });
  return selected.length > 0 ? selected.join(", ") : "";
}

const parseTextInput = (group) => {
  const input = group.querySelector("input[type='text'], textarea");
  return input?.value.trim() || "";
}

const parseSelectAnswer = (group) => {
  const select = group.querySelector("select");
  return select?.value.trim() || "";
}

const parseRadio = (group) => {
  const radio = group.querySelector("input[name='option']:checked");
  return radio.value;
}

/* ---------------------------------------------------
 * 2. DATA MODELS
 * --------------------------------------------------- */

// Stores basic customer contact info
class Customer {
  constructor({ firstName, lastName, email, phone, companyName, jobTitle }) {
    this.firstName = firstName;
    this.lastName = lastName;
    this.email = email;
    this.phone = phone;
    this.companyName = companyName;
    this.jobTitle = jobTitle;
  }
}

// Represents a question/answer from a dynamic form block
class FormQuestion {
  constructor({ question, answer, type }) {
    this.formQuestion = question;
    this.formAnswer = answer;
    this.fieldType = type;
  }

  toJSON() {
    return {
      formQuestion: this.formQuestion,
      formAnswer: this.formAnswer,
      fieldType: this.fieldType
    };
  }
}

// Stores NetSuite-required metadata (ID, IP, form type)
class NetSuiteBody {
  constructor({ submissionID, ipAddress, onlineFormType }) {
    this.formSubmissionID = submissionID;
    this.submissionIP = ipAddress;
    this.onlineFormType = onlineFormType;
  }
}

// Captures UTM and ad tracking metadata
class ConversionTracking {
  constructor({
    googleAd = {},
    utm = {},
    eventTimestamp = "",
    orderId = "",
    orderTimestamp = "",
    store = "",
    type = "",
    gat_origin = "",
    customerOrderCount = "",
    env = ""
  } = {}) {
    this.googleAd = {
      gclid: "",
      landingPage: "",
      campaign: "",
      adGroup: "",
      type: "",
      ...cleanNulls(googleAd)
    };
    this.utm = {
      source: "",
      medium: "",
      campaign: "",
      content: "",
      term: "",
      landingPage: "",
      type: "",
      eventTimestamp: "",
      expires: "",
      ...cleanNulls(utm)
    };
    this.eventTimestamp = eventTimestamp;
    this.orderId = orderId;
    this.orderTimestamp = orderTimestamp;
    this.store = store;
    this.type = type;
    this.gat_origin = gat_origin;
    this.customerOrderCount = customerOrderCount;
    this.env = env;
  }
}


/* ---------------------------------------------------
 * 3. PAYLOAD BUILDER
 * --------------------------------------------------- */

// Fluent builder for assembling full NetSuite form payload
class NetSuitePayloadBuilder {
  constructor({ environment }) {
    this.env = environment;
    this.action = "submitWebForm";
  }

  setBody(bodyObj) {
    this.body = cleanNulls(bodyObj);
    return this;
  }

  setCustomer(customerObj) {
    this.body.formValues = {
      ...this.body.formValues,
      ...cleanNulls(customerObj)
    };
    return this;
  }

  setConversionTracking(conversionTrackingObj) {
    this.conversionTracking = cleanNulls(conversionTrackingObj);
    return this;
  }

  addFormQuestion(questionObj) {
    this.body.formValues.formQuestions = [
      ...(this.body.formValues.formQuestions || []),
      questionObj.toJSON()
    ];
    return this;
  }

  // Parses .form-question-group elements and adds answers to the payload
  getFormQuestions() {
  const formQuestions = document.querySelectorAll('.form-question-group');

  formQuestions.forEach(group => {
    const question = group.dataset.question;
    const type = group.dataset.fieldType;
    let answer = "";

    if (group.querySelector("input[type='checkbox']")) {
      answer = parseCheckboxAnswers(group);
    } else if (group.querySelector("textarea") || group.querySelector("input[type='text']")) {
      answer = parseTextInput(group);
    } else if (group.querySelector("select")) {
      answer = parseSelectAnswer(group);
    } else if (group.querySelector("input[name='option']:checked")) {
      answer = parseRadio(group);
    }

    if (answer) {
      this.addFormQuestion(new FormQuestion({ question, answer, type }));
    }
  });

  return this;
}


  build() {
    return this;
  }
}


/* ---------------------------------------------------
 * 4. MAIN FORM CONTROLLER
 * --------------------------------------------------- */

class FormHandler {
  constructor({ formId, endpoint, authToken, settingsEnvironment, confirmationModalMessage }) {
    this.form = document.getElementById(formId);
    this.endpoint = endpoint;
    this.authToken = authToken;
    this.settingsEnvironment = settingsEnvironment;
    this.confirmationModalMessage = confirmationModalMessage;
    this.eventLabel = this.form.dataset.conversionTrackingEventLabel;
    this.formType = this.form.dataset.formType;
    this.urlRedirect = this.form.dataset.urlRedirect;
  }

  // Anti-spam: checkbox honeypot detection
  checkHoneyPot() {
    const checkbox = this.form.querySelector('#contact_me_by_fax_only');
    return checkbox?.checked ?? false;
  }

  // Anti-spam: time-based check (must spend >5s on form)
  checkTimer() {
    const timestamp = this.form.querySelector('#form_timestamp');
    const loadTime = parseInt(timestamp?.value || 0, 10);
    return (Date.now() - loadTime) / 1000 <= 5;
  }

  // Returns true if either honeypot or timer check fails
  checkSpam() {
    return this.checkHoneyPot() || this.checkTimer();
  }

  // Builds the NetSuite API payload for this form
  buildPayload(formType) {
    const SUBMISSION_ID = generateUniqueId(); // Generate unique form ID
    const IP_ADDRESS = getIpAddress() || ""; // Fetch IP from client
    const formData = new FormData(this.form);
    const data = Object.fromEntries(formData.entries());
    const gclidData = JSON.parse(data.custevent_gclid || "{}");

    const customer = new Customer(data); // Construct customer info
    const conversionTracking = new ConversionTracking(gclidData);  // Set up UTM/ad tracking metadata

    // Define NetSuite body structure
    const formBody = new NetSuiteBody({
      submissionID: SUBMISSION_ID,
      ipAddress: IP_ADDRESS,
      onlineFormType: formType
    });

    // Build final payload with all data
    return new NetSuitePayloadBuilder({ environment: "production" })
      .setBody(formBody)
      .setCustomer(customer)
      .setConversionTracking(conversionTracking)
      .getFormQuestions(); // Automatically parses form-question-group blocks
  }

  // Creates GTM-compatible object for pushing analytics to `dataLayer`
  buildGclidPayload(payload) {
    const body = payload?.body || {};
    const conversion = payload?.conversionTracking || {};
    const utm = conversion.utm || {};
    const ipAddress = body.submissionIP || "";

    return {
      event: this.eventLabel,
      data: {
        form_name: body.onlineFormType || "",
        utmType: utm.type || "",
        term: utm.term || "",
        content: utm.content || "",
        campaign: utm.campaign || "",
        medium: utm.medium || "",
        source: utm.source || "",
        ipAddress: ipAddress
      },
      formType: "netsuite"
    };
  }
  // Sends the fetch request and parses response (with double-parse logic)
  async sendPayload(payload) {
    const response = await fetch(this.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": this.authToken
      },
      body: JSON.stringify(payload)
    });

    let body = await response.json();

    if (typeof body === "string") {
      try {
        body = JSON.parse(body);
      } catch (e) {
        console.error("Double-parse failed:", e.message);
      }
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${JSON.stringify(body)}`);
    }

    return body;
  }

  // Shows loading modal
  showSubmissionModal() {
    jQuery.noConflict();
    $("#submissionNetsuiteForm").modal("show");
  }

  // Hides modal and optionally redirects
  handleSuccessRedirect() {
    $('#submissionNetsuiteForm').modal('hide');
    $("#confirmationModal").modal("show");

    if (this.urlRedirect) {
      setTimeout(() => {
        window.location.href = this.urlRedirect;
      }, 300);
    }
  }

  // Logs analytics if in production
  pushAnalytics(payload) {
    if (this.settingsEnvironment === 'production') {
      window.dataLayer.push(payload);
    }
  }

  // Logs debug output in stage
  logDebug(payload, gclidPayload) {
    if (this.settingsEnvironment === 'stage') {
      console.log("Payload:", payload);
      console.log("GCLID Payload", gclidPayload);
    }
  }

  // Handles submit event: spam check, payload build, NetSuite POST, GTM tracking
  async handleSubmit(e) {
    e.preventDefault();

    const spamDetected = this.checkSpam();
    const formType = spamDetected ? `${this.formType}_spam_detected` : this.formType;
    const payload = this.buildPayload(formType);
    const gclidPayload = this.buildGclidPayload(payload);
    // move properties within data object to be on the root level of this payload

    this.logDebug(payload, gclidPayload);
    this.pushAnalytics(gclidPayload);

    try {
      this.showSubmissionModal();

      const responseBody = await this.sendPayload(payload);
      console.log("Submitted:", responseBody);

      const isSuccess = responseBody.success === true || responseBody.success === "true";
      console.log("isSuccess:", isSuccess);

      if (isSuccess) {
        this.handleSuccessRedirect();
      }

    } catch (err) {
      console.error("Form submission failed:", err.message);
      alert("Something went wrong while submitting the form. Please try again.");
    }
  }

  // Initializes the form on page load: sets timer, binds submit handler
  init() {
    if (!this.form) return;

    const timestamp = this.form.querySelector('#form_timestamp');
    if (timestamp) timestamp.value = Date.now();

    this.form.addEventListener('submit', this.handleSubmit.bind(this));

    // For debug access
    if (this.settingsEnvironment === 'stage') {
      window._zymoFormHandler = this;
      window._zymoBuildPayload = () => this.buildPayload(this.formType);
      window._zymoBuildGclidPayload = () => this.buildGclidPayload(this.buildPayload(this.formType));
      console.log('%c[Zymo Debug] Access form payload via _zymoBuildPayload() or _zymoBuildGclidPayload()', 'color: green;');
    }
  }
}